/*
 * Use IndexedDB as a cache, since it allows more storage space.
 * Use Map to serve data with sync, save everything to IndexedDB.
 *
 * https://developer.mozilla.org/en-US/docs/Web/API/IndexedDB_API/Browser_storage_limits_and_eviction_criteria
 */
import StorageCache from './StorageCache';

class IDBMap extends Map {
    constructor(...args) {
        super(args)
        this.connection = new Promise((resolve, reject) => {
            if (!this.open) {
                this.open = indexedDB.open('CorefactCache', 1);
                this.open.onerror = () => reject(this.open.error);
                this.open.onsuccess = () => resolve(this.open.result);
                this.open.onupgradeneeded = () => {
                    this.open.result.createObjectStore('Cache');
                };
            } else {
                return resolve(this.open.result);
            }
        });

        this._keys().then(async (keys) => {
            for (let key of keys) {
                super.set(key, await this._getItem(key));
            }
        });
    }

    get supported() { return ('indexedDB' in window) }

    query(type, fn) {
        return this.connection.then(db => new Promise((resolve, reject) => {
            let objStore;
            const transaction = db.transaction('Cache', type);
            transaction.oncomplete = () => resolve(objStore);
            transaction.onabort = transaction.onerror = () => reject(transaction.error);
            objStore = fn(transaction.objectStore('Cache'));
        }));
    }

    getItem(key) {
        return super.get(key);
    }

    _getItem(key) {
        return this.query('readonly', store => store.get(key)).then(getter => getter?.result);
    }

    setItem(key, value) {
        this.query('readwrite', store => store.put(value, key));
        return super.set(key, value);
    }

    removeItem(key) {
        this.query('readwrite', store => store.delete(key));
        return super.delete(key);
    }

    clear() {
        this.query('readwrite', store => store.clear());
        return super.clear();
    }

    _keys() {
        const keys = [];
        return this.query('readonly', store => (store.openKeyCursor || store.openCursor).call(store).onsuccess = function () {
            if (!this.result) return;
            keys.push(this.result.key);
            this.result.continue();
        }).then(() => keys);
    }
}

/*
 * Works the same way as StorageCache, just saves to IndexedDB.
 */
export default class IndexedDBCache extends StorageCache {
    constructor(limit = 10, storage = sessionStorage, db = 'db', type = 'indexdb') {
        super(limit, storage, db, type);
        this.storage = new IDBMap();
    }

    get size() {
        return this.storage.size;
    }

    clear() {
        return this.storage.clear();
    }

    stamps(clean = true) {
        return Array.from(this.storage.keys()).filter(key => {
            return String(key).indexOf(`${this.tstamp}`) === 0
        })[clean ? 'map' : 'valueOf'](key => key.replace(new RegExp(`${this.tstamp}`), ''));
    }

    keys(clean = true) {
        return Array.from(this.storage.keys()).filter(key => this.hasPrefix(key)).sort((a, b) => {
            return this.getTstamp(a, true) - this.getTstamp(b, true);
        })[clean ? 'map' : 'valueOf'](key => key.replace(new RegExp(`${this.prefix}`), ''));
    }
}
